import React, { useContext, useReducer, useRef, useEffect, useCallback, useState } from 'react';
import {
	VideosContext,
	videosInitialState,
	IFilterValues,
	TVideosCountOfItems,
	TVideosSortBy,
	TTypeOfModalWindow,
} from './videosContext';
import { AppContext, IAppContext } from 'appState/appContext';
import { videosReducer } from './videosReducer';
import { VideosActionTypes } from './videosActions';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedVideos, deleteVideo } from 'backend/videos';
import { AxiosResponse } from 'axios';
import { IVideos, IGetPaginatedVideos } from 'types';

const GamesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(videosReducer, videosInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IVideos>, IGetPaginatedVideos>(
		isMounted.current ? getPaginatedVideos : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			videoStudioId: state.selectedStudio,
			videoCategoryId: state.selectedCategory,
			isUnassignedVideos: state.isUnassignedVideos,
			sortByName: state.sortBy === 'NameA-Z' || state.sortBy === 'NameZ-A' ? state.sortBy : null,
			sortByStudios: state.sortBy === 'StudiosA-Z' || state.sortBy === 'StudiosZ-A' ? state.sortBy : null,
			sortByCategories:
				state.sortBy === 'CategoriesA-Z' || state.sortBy === 'CategoriesZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: VideosActionTypes.SET_VIDEOS,
				payload: {
					videos: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteVideo(state.IDForDeletion);
			const filteredVideos = state.videos.videos.filter((el) => el.id !== state.IDForDeletion);
			dispatch({
				type: VideosActionTypes.SET_VIDEOS,
				payload: {
					videos: {
						videosCount: state.videos.videosCount - 1,
						videos: filteredVideos,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: VideosActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.IDForDeletion, state.videos]);

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: VideosActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setStudio = (value: Nullable<string>) => {
		dispatch({
			type: VideosActionTypes.SET_STUDIO,
			payload: {
				selectedStudio: value,
			},
		});
	};

	const setCategory = (value: Nullable<string>) => {
		dispatch({
			type: VideosActionTypes.SET_CATEGORY,
			payload: {
				selectedCategory: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: VideosActionTypes.RESET_SELECTED_CONTROLS });

	const toggleUnassignedVideos = () => {
		dispatch({ type: VideosActionTypes.TOGGLE_UNASSIGNED_VIDEOS });
	};

	const setSortBy = (sortBy: TVideosSortBy) => {
		dispatch({
			type: VideosActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TVideosCountOfItems) => {
		dispatch({
			type: VideosActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: VideosActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: VideosActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const setIDForFullInfo = (value: string) => {
		dispatch({
			type: VideosActionTypes.SET_ID_FOR_FULL_INFO,
			payload: {
				IDForFullInfo: value,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: VideosActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: VideosActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<VideosContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setStudio,
				setCategory,
				resetSelectedControls,
				toggleUnassignedVideos,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setIDForDeletion,
				setIDForFullInfo,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				videos: state.videos,
				searchString: state.searchString,
				selectedStudio: state.selectedStudio,
				selectedCategory: state.selectedCategory,
				isUnassignedVideos: state.isUnassignedVideos,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				IDForFullInfo: state.IDForFullInfo,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</VideosContext.Provider>
	);
};

export default GamesState;
